import Ajv, { type ErrorObject } from 'ajv';
import {
  normalizePromptSchemaFieldName,
  RESERVED_PROMPT_SCHEMA_FIELD_NAME_CHARACTERS,
  decodeVideoLevelNestedFieldName,
  canonicalizePromptSemanticSearchFieldName,
  encodePromptSemanticSearchFieldPathSegments,
} from '../../utils/promptSemanticFieldPaths';
export {
  canonicalizePromptSemanticSearchFieldName,
  canonicalizePromptSemanticSearchFieldPath,
  encodePromptSemanticSearchFieldPathSegments,
  encodePromptSemanticSearchFieldInstanceKey,
  decodePromptSemanticSearchFieldInstanceKey,
} from '../../utils/promptSemanticFieldPaths';
export type { PromptSemanticSearchFieldInstanceToken } from '../../utils/promptSemanticFieldPaths';

export type PromptSchemaEnumValue = string | number | boolean;
export const IMPLICIT_OPEN_ADDITIONAL_PROPERTIES = '__builder_implicit_open__' as const;
export type PromptSchemaAdditionalProperties =
  | boolean
  | typeof IMPLICIT_OPEN_ADDITIONAL_PROPERTIES
  | Record<string, any>
  | undefined;

export type PromptSchemaFieldType =
  | 'any'
  | 'string'
  | 'integer'
  | 'number'
  | 'boolean'
  | 'enum'
  | 'object'
  | 'array_string'
  | 'array_enum'
  | 'array_integer'
  | 'array_number'
  | 'array_boolean'
  | 'array_object';

export interface PromptSchemaFieldAdvancedOptions {
  minLength?: number | string;
  maxLength?: number | string;
  pattern?: string;
  format?: string;
  minimum?: number | string;
  maximum?: number | string;
  minItems?: number | string;
  maxItems?: number | string;
  uniqueItems?: boolean;
  minProperties?: number | string;
  maxProperties?: number | string;
}

export interface PromptSchemaFieldDefinition {
  id: string;
  name: string;
  type: PromptSchemaFieldType;
  description: string;
  required: boolean;
  allowAdditionalProperties?: PromptSchemaAdditionalProperties;
  defaultValue?: any;
  enumValues?: PromptSchemaEnumValue[];
  advancedOptions?: PromptSchemaFieldAdvancedOptions;
  nestedFields?: PromptSchemaFieldDefinition[];
  preserveAsHiddenRequired?: boolean;
  sourceSchema?: Record<string, any>;
}

export type PromptSchemaFieldStyleCategory =
  | 'string'
  | 'number'
  | 'boolean'
  | 'array'
  | 'object'
  | 'default';

export interface PromptSchemaDisplayField {
  name: string;
  description: string;
  required: boolean;
  type: PromptSchemaFieldType;
  typeLabel: string;
  compactTypeLabel: string;
  styleCategory: PromptSchemaFieldStyleCategory;
}

export interface PromptSchemaDisplayFieldWithNesting extends PromptSchemaDisplayField {
  depth: number;
  nestedFields?: PromptSchemaDisplayFieldWithNesting[];
}

export interface PromptSemanticIndexingLeafFieldOption {
  fieldPath: string;
  displayLabel: string;
  type: PromptSchemaFieldType;
  warnsForSemanticSearch: boolean;
}

export interface PromptSemanticIndexingFieldSummary {
  totalLeafFields: number;
  enabledLeafFields: number;
  disabledLeafFields: number;
  dynamicFieldsAlwaysEmbedded: boolean;
}

const createFieldId = (): string => {
  const randomSuffix = typeof globalThis !== 'undefined'
    && typeof globalThis.crypto?.randomUUID === 'function'
    ? globalThis.crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

  return `field_${randomSuffix}`;
};

const hasOwn = (value: object, key: string): boolean => (
  Object.prototype.hasOwnProperty.call(value, key)
);

const promptSchemaDraft7Validator = new Ajv({
  allErrors: true,
  strict: false,
  validateSchema: true,
});

const formatDraft7SchemaValidationError = (
  errors: ErrorObject[] | null | undefined,
): string => {
  const firstError = errors?.[0];
  if (!firstError) {
    return 'Schema is not a valid Draft 7 JSON schema';
  }

  const instancePath = firstError.instancePath
    .split('/')
    .filter(Boolean)
    .join('.');
  const schemaPath = instancePath ? `Schema.${instancePath}` : 'Schema';
  return `${schemaPath} ${firstError.message || 'is not a valid Draft 7 JSON schema'}`;
};

const formatDraft7ValueValidationError = (
  errors: ErrorObject[] | null | undefined,
  fieldPath: string,
): string => {
  const firstError = errors?.[0];
  if (!firstError) {
    return `${fieldPath} is invalid`;
  }

  const instancePath = firstError.instancePath
    .split('/')
    .filter(Boolean)
    .reduce((normalizedPath, segment) => {
      if (/^\d+$/.test(segment)) {
        return `${normalizedPath}[${segment}]`;
      }
      return `${normalizedPath}.${segment}`;
    }, fieldPath);

  return `${instancePath} ${firstError.message || 'is invalid'}`;
};

const validateDraft7SchemaValue = (
  schema: Record<string, any>,
  value: unknown,
  fieldPath: string,
): string | null => {
  const isValidValue = promptSchemaDraft7Validator.validate(schema, value);
  if (isValidValue) {
    return null;
  }

  return formatDraft7ValueValidationError(promptSchemaDraft7Validator.errors, fieldPath);
};

export const clonePromptSchemaAdditionalProperties = (
  value: PromptSchemaAdditionalProperties,
): PromptSchemaAdditionalProperties => {
  if (value === IMPLICIT_OPEN_ADDITIONAL_PROPERTIES) {
    return value;
  }

  if (value && typeof value === 'object') {
    return JSON.parse(JSON.stringify(value));
  }

  return value;
};

const cloneSchemaFragment = <T>(value: T): T => {
  if (value === undefined) {
    return value;
  }

  return JSON.parse(JSON.stringify(value)) as T;
};

const isPlainObject = (value: unknown): value is Record<string, any> => (
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)
);

const normalizeJsonLikeValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map((entry) => normalizeJsonLikeValue(entry));
  }

  if (isPlainObject(value)) {
    return Object.keys(value)
      .sort()
      .reduce<Record<string, unknown>>((normalizedValue, key) => {
        normalizedValue[key] = normalizeJsonLikeValue(value[key]);
        return normalizedValue;
      }, {});
  }

  return value;
};

const areJsonLikeValuesEqual = (left: unknown, right: unknown): boolean => (
  JSON.stringify(normalizeJsonLikeValue(left)) === JSON.stringify(normalizeJsonLikeValue(right))
);

const BUILDER_MANAGED_SCHEMA_KEYS = new Set([
  'type',
  'description',
  'default',
  'enum',
  'minLength',
  'maxLength',
  'pattern',
  'format',
  'minimum',
  'maximum',
  'minItems',
  'maxItems',
  'uniqueItems',
  'minProperties',
  'maxProperties',
  'properties',
  'required',
  'additionalProperties',
  'items',
]);

const isObjectFieldType = (type: PromptSchemaFieldType): boolean => (
  type === 'object' || type === 'array_object'
);

const RESERVED_PROMPT_SCHEMA_FIELD_NAMES = new Set(['__pydantic_extra__']);

const getPromptSchemaFieldNameValidationError = (
  fieldName: string,
  schemaName: string,
  options: {
    includeContextLabel?: boolean;
  } = {},
): string | null => {
  const prefix = options.includeContextLabel === false ? '' : `${schemaName} `;
  if (RESERVED_PROMPT_SCHEMA_FIELD_NAMES.has(fieldName)) {
    return `${prefix}field name "${fieldName}" is reserved and cannot be used`;
  }

  if (RESERVED_PROMPT_SCHEMA_FIELD_NAME_CHARACTERS.some((character) => fieldName.includes(character))) {
    return `${prefix}field name "${fieldName}" cannot contain '.', '[' or ']'`;
  }

  return null;
};

const isLeafPromptSchemaFieldType = (type: PromptSchemaFieldType): boolean => (
  type !== 'object' && type !== 'array_object'
);

export const isPromptSemanticSearchScalarFieldType = (
  type: PromptSchemaFieldType,
): boolean => (
  type === 'integer'
  || type === 'number'
  || type === 'boolean'
  || type === 'array_integer'
  || type === 'array_number'
  || type === 'array_boolean'
);

export const formatPromptSemanticSearchFieldLabel = (
  fieldName: string,
): string => {
  const canonical = canonicalizePromptSemanticSearchFieldName(fieldName);
  const nested = decodeVideoLevelNestedFieldName(canonical);
  return nested ? nested.join(' -> ') : canonical;
};

export const collectPromptSemanticIndexingLeafFieldOptions = (
  fields: PromptSchemaFieldDefinition[],
  parentPath: string[] = [],
): PromptSemanticIndexingLeafFieldOption[] => fields.flatMap((field) => {
  const normalizedFieldName = normalizePromptSchemaFieldName(field.name);
  if (!normalizedFieldName) {
    return [];
  }

  const nextPath = [...parentPath, normalizedFieldName];
  if (!isLeafPromptSchemaFieldType(field.type)) {
    return collectPromptSemanticIndexingLeafFieldOptions(field.nestedFields || [], nextPath);
  }

  const fieldPath = encodePromptSemanticSearchFieldPathSegments(nextPath);
  return [{
    fieldPath,
    displayLabel: formatPromptSemanticSearchFieldLabel(fieldPath),
    type: field.type,
    warnsForSemanticSearch: isPromptSemanticSearchScalarFieldType(field.type),
  }];
});

export const collectPromptSemanticIndexingLeafFieldOptionsFromSchema = (
  schema: Record<string, any>,
): PromptSemanticIndexingLeafFieldOption[] => (
  collectPromptSemanticIndexingLeafFieldOptions(schemaToBuilderPromptSchemaFields(schema))
);

export const sanitizePromptSemanticIndexingDisabledFields = (
  disabledFieldNames: string[],
  fields: PromptSchemaFieldDefinition[],
): string[] => {
  const allowedFieldNames = new Set(
    collectPromptSemanticIndexingLeafFieldOptions(fields).map((field) => field.fieldPath),
  );
  const seenFieldNames = new Set<string>();

  return disabledFieldNames.reduce<string[]>((nextFieldNames, rawFieldName) => {
    const fieldName = canonicalizePromptSemanticSearchFieldName(rawFieldName);
    if (!fieldName || seenFieldNames.has(fieldName) || !allowedFieldNames.has(fieldName)) {
      return nextFieldNames;
    }

    seenFieldNames.add(fieldName);
    nextFieldNames.push(fieldName);
    return nextFieldNames;
  }, []);
};

export const sanitizePromptSemanticIndexingDisabledFieldsFromSchema = (
  disabledFieldNames: string[],
  schema: Record<string, any>,
): string[] => (
  sanitizePromptSemanticIndexingDisabledFields(
    disabledFieldNames,
    schemaToBuilderPromptSchemaFields(schema),
  )
);

export const getPromptSemanticIndexingFieldSummary = (
  disabledFieldNames: string[],
  fields: PromptSchemaFieldDefinition[],
  options: { allowAdditionalProperties?: PromptSchemaAdditionalProperties } = {},
): PromptSemanticIndexingFieldSummary => {
  const leafFieldOptions = collectPromptSemanticIndexingLeafFieldOptions(fields);
  const normalizedDisabledFieldNames = new Set(
    sanitizePromptSemanticIndexingDisabledFields(disabledFieldNames, fields),
  );
  const totalLeafFields = leafFieldOptions.length;
  const disabledLeafFields = leafFieldOptions.filter(
    (field) => normalizedDisabledFieldNames.has(field.fieldPath),
  ).length;
  const enabledLeafFields = totalLeafFields - disabledLeafFields;
  const dynamicFieldsAlwaysEmbedded = isAdditionalPropertiesEnabled(options.allowAdditionalProperties);

  return {
    totalLeafFields,
    enabledLeafFields,
    disabledLeafFields,
    dynamicFieldsAlwaysEmbedded,
  };
};

export const hasPromptSemanticIndexingEmbeddableCapacity = (
  disabledFieldNames: string[],
  fields: PromptSchemaFieldDefinition[],
  options: { allowAdditionalProperties?: PromptSchemaAdditionalProperties } = {},
): boolean => {
  const summary = getPromptSemanticIndexingFieldSummary(disabledFieldNames, fields, options);
  return summary.enabledLeafFields > 0 || summary.dynamicFieldsAlwaysEmbedded;
};

export const getPromptSemanticIndexingValidationError = (
  disabledFieldNames: string[],
  fields: PromptSchemaFieldDefinition[],
  options: {
    allowAdditionalProperties?: PromptSchemaAdditionalProperties;
    surfaceLabel?: string;
  } = {},
): string | null => {
  if (hasPromptSemanticIndexingEmbeddableCapacity(disabledFieldNames, fields, options)) {
    return null;
  }

  const surfaceLabel = options.surfaceLabel || 'schema';
  return `Select at least one searchable leaf field for the ${surfaceLabel}`;
};

const cloneEnumValues = (enumValues?: PromptSchemaEnumValue[]): PromptSchemaEnumValue[] => (
  [...(enumValues || [])]
);

const clonePromptSchemaFieldAdvancedOptions = (
  value?: PromptSchemaFieldAdvancedOptions,
): PromptSchemaFieldAdvancedOptions | undefined => (
  value ? JSON.parse(JSON.stringify(value)) as PromptSchemaFieldAdvancedOptions : undefined
);

const compactPromptSchemaFieldAdvancedOptions = (
  value?: PromptSchemaFieldAdvancedOptions,
): PromptSchemaFieldAdvancedOptions | undefined => {
  if (!value) {
    return undefined;
  }

  const nextValue: PromptSchemaFieldAdvancedOptions = {};
  const optionKeys: (keyof PromptSchemaFieldAdvancedOptions)[] = [
    'minLength',
    'maxLength',
    'pattern',
    'format',
    'minimum',
    'maximum',
    'minItems',
    'maxItems',
    'uniqueItems',
    'minProperties',
    'maxProperties',
  ];

  optionKeys.forEach((key) => {
    const optionValue = value[key];
    if (optionValue === undefined || optionValue === '' || optionValue === false) {
      return;
    }

    (
      nextValue as Record<
        keyof PromptSchemaFieldAdvancedOptions,
        PromptSchemaFieldAdvancedOptions[keyof PromptSchemaFieldAdvancedOptions] | undefined
      >
    )[key] = optionValue;
  });

  return Object.keys(nextValue).length > 0 ? nextValue : undefined;
};

const INTEGER_DEFAULT_PATTERN = /^[+-]?\d+$/;
const NUMBER_DEFAULT_PATTERN = /^[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?$/;
const NON_NEGATIVE_INTEGER_PATTERN = /^(?:0|[1-9]\d*)$/;

const parseIntegerDefaultValue = (value: unknown): number | undefined => {
  if (typeof value === 'number') {
    return Number.isInteger(value) ? value : undefined;
  }

  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmedValue = value.trim();
  if (!INTEGER_DEFAULT_PATTERN.test(trimmedValue)) {
    return undefined;
  }

  const parsedValue = Number.parseInt(trimmedValue, 10);
  return Number.isNaN(parsedValue) ? undefined : parsedValue;
};

const parseNumberDefaultValue = (value: unknown): number | undefined => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }

  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmedValue = value.trim();
  if (!NUMBER_DEFAULT_PATTERN.test(trimmedValue)) {
    return undefined;
  }

  const parsedValue = Number(trimmedValue);
  return Number.isFinite(parsedValue) ? parsedValue : undefined;
};

const parseNonNegativeIntegerOption = (value: unknown): number | undefined => {
  if (typeof value === 'number') {
    return Number.isInteger(value) && value >= 0 ? value : undefined;
  }

  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmedValue = value.trim();
  if (!NON_NEGATIVE_INTEGER_PATTERN.test(trimmedValue)) {
    return undefined;
  }

  const parsedValue = Number.parseInt(trimmedValue, 10);
  return Number.isNaN(parsedValue) ? undefined : parsedValue;
};

const parseFiniteNumberOption = (value: unknown): number | undefined => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }

  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmedValue = value.trim();
  if (!NUMBER_DEFAULT_PATTERN.test(trimmedValue)) {
    return undefined;
  }

  const parsedValue = Number(trimmedValue);
  return Number.isFinite(parsedValue) ? parsedValue : undefined;
};

const cloneSourceSchema = (
  sourceSchema?: Record<string, any>,
): Record<string, any> | undefined => (
  sourceSchema ? cloneSchemaFragment(sourceSchema) : undefined
);

const projectBuilderManagedSchema = (value: unknown): unknown => {
  if (!isPlainObject(value)) {
    return cloneSchemaFragment(value);
  }

  return Object.keys(value).reduce<Record<string, unknown>>((projectedValue, key) => {
    if (!BUILDER_MANAGED_SCHEMA_KEYS.has(key)) {
      return projectedValue;
    }

    if (key === 'properties' && isPlainObject(value.properties)) {
      projectedValue.properties = Object.entries(value.properties).reduce<Record<string, unknown>>(
        (projectedProperties, [propertyName, propertySchema]) => {
          projectedProperties[propertyName] = projectBuilderManagedSchema(propertySchema);
          return projectedProperties;
        },
        {},
      );
      return projectedValue;
    }

    if (key === 'items') {
      projectedValue.items = projectBuilderManagedSchema(value.items);
      return projectedValue;
    }

    if (key === 'additionalProperties' && isPlainObject(value.additionalProperties)) {
      projectedValue.additionalProperties = projectBuilderManagedSchema(value.additionalProperties);
      return projectedValue;
    }

    projectedValue[key] = cloneSchemaFragment(value[key]);
    return projectedValue;
  }, {});
};

const getSchemaShapeKey = (schema: Record<string, any>): string => {
  const schemaType = typeof schema.type === 'string' ? schema.type : 'any';
  if (schemaType !== 'array') {
    return schemaType;
  }

  const itemType = isPlainObject(schema.items) && typeof schema.items.type === 'string'
    ? schema.items.type
    : 'any';

  return `array:${itemType}`;
};

const areAllStringEnumValues = (enumValues: unknown[]): enumValues is string[] => (
  enumValues.every((value) => typeof value === 'string')
);

const getStringEnumValues = (enumValues?: PromptSchemaEnumValue[]): string[] => (
  (enumValues || [])
    .filter((value): value is string => typeof value === 'string')
);

const hasCompatibleScalarEnumValues = (
  enumValues: PromptSchemaEnumValue[] | undefined,
  fieldType: 'string' | 'integer' | 'number' | 'boolean',
): boolean => {
  if (!enumValues || enumValues.length === 0) {
    return false;
  }

  switch (fieldType) {
    case 'string':
      return enumValues.every((value) => typeof value === 'string');
    case 'integer':
      return enumValues.every((value) => Number.isInteger(value));
    case 'number':
      return enumValues.every((value) => typeof value === 'number' || Number.isInteger(value));
    case 'boolean':
      return enumValues.every((value) => typeof value === 'boolean');
    default:
      return false;
  }
};

const assignNonNegativeIntegerSchemaKeyword = (
  schema: Record<string, any>,
  key: 'minLength' | 'maxLength' | 'minItems' | 'maxItems' | 'minProperties' | 'maxProperties',
  value: unknown,
): void => {
  const parsedValue = parseNonNegativeIntegerOption(value);
  if (parsedValue !== undefined) {
    schema[key] = parsedValue;
  }
};

const assignFiniteNumberSchemaKeyword = (
  schema: Record<string, any>,
  key: 'minimum' | 'maximum',
  value: unknown,
): void => {
  const parsedValue = parseFiniteNumberOption(value);
  if (parsedValue !== undefined) {
    schema[key] = parsedValue;
  }
};

const applyStringSchemaAdvancedOptions = (
  schema: Record<string, any>,
  advancedOptions?: PromptSchemaFieldAdvancedOptions,
): void => {
  if (!advancedOptions) {
    return;
  }

  assignNonNegativeIntegerSchemaKeyword(schema, 'minLength', advancedOptions.minLength);
  assignNonNegativeIntegerSchemaKeyword(schema, 'maxLength', advancedOptions.maxLength);

  if (typeof advancedOptions.pattern === 'string' && advancedOptions.pattern !== '') {
    schema.pattern = advancedOptions.pattern;
  }

  if (typeof advancedOptions.format === 'string' && advancedOptions.format.trim() !== '') {
    schema.format = advancedOptions.format.trim();
  }
};

const applyNumberSchemaAdvancedOptions = (
  schema: Record<string, any>,
  advancedOptions?: PromptSchemaFieldAdvancedOptions,
): void => {
  if (!advancedOptions) {
    return;
  }

  assignFiniteNumberSchemaKeyword(schema, 'minimum', advancedOptions.minimum);
  assignFiniteNumberSchemaKeyword(schema, 'maximum', advancedOptions.maximum);
};

const applyArraySchemaAdvancedOptions = (
  schema: Record<string, any>,
  advancedOptions?: PromptSchemaFieldAdvancedOptions,
): void => {
  if (!advancedOptions) {
    return;
  }

  assignNonNegativeIntegerSchemaKeyword(schema, 'minItems', advancedOptions.minItems);
  assignNonNegativeIntegerSchemaKeyword(schema, 'maxItems', advancedOptions.maxItems);
  if (advancedOptions.uniqueItems) {
    schema.uniqueItems = true;
  }
};

const applyObjectSchemaAdvancedOptions = (
  schema: Record<string, any>,
  advancedOptions?: PromptSchemaFieldAdvancedOptions,
): void => {
  if (!advancedOptions) {
    return;
  }

  assignNonNegativeIntegerSchemaKeyword(schema, 'minProperties', advancedOptions.minProperties);
  assignNonNegativeIntegerSchemaKeyword(schema, 'maxProperties', advancedOptions.maxProperties);
};

const applyArrayItemSchemaAdvancedOptions = (
  schema: Record<string, any>,
  fieldType: PromptSchemaFieldType,
  advancedOptions?: PromptSchemaFieldAdvancedOptions,
): void => {
  if (!advancedOptions) {
    return;
  }

  if (fieldType === 'array_string' || fieldType === 'array_enum') {
    applyStringSchemaAdvancedOptions(schema, advancedOptions);
    return;
  }

  if (fieldType === 'array_integer' || fieldType === 'array_number') {
    applyNumberSchemaAdvancedOptions(schema, advancedOptions);
    return;
  }

  if (fieldType === 'array_object') {
    applyObjectSchemaAdvancedOptions(schema, advancedOptions);
  }
};

const buildFieldSchemaFragment = (
  field: PromptSchemaFieldDefinition,
): Record<string, any> => {
  const fieldSchema: Record<string, any> = {};
  const advancedOptions = compactPromptSchemaFieldAdvancedOptions(field.advancedOptions);
  if (field.description !== '') {
    fieldSchema.description = field.description;
  }

  switch (field.type) {
    case 'any':
      if (field.defaultValue !== undefined) {
        fieldSchema.default = cloneSchemaFragment(field.defaultValue);
      }
      break;
    case 'string':
      fieldSchema.type = 'string';
      if (hasCompatibleScalarEnumValues(field.enumValues, 'string')) {
        fieldSchema.enum = cloneEnumValues(field.enumValues);
      }
      if (field.defaultValue !== undefined) {
        fieldSchema.default = field.defaultValue;
      }
      applyStringSchemaAdvancedOptions(fieldSchema, advancedOptions);
      break;
    case 'integer':
      fieldSchema.type = 'integer';
      if (hasCompatibleScalarEnumValues(field.enumValues, 'integer')) {
        fieldSchema.enum = cloneEnumValues(field.enumValues);
      }
      if (field.defaultValue !== undefined && field.defaultValue !== '') {
        const parsedDefaultValue = parseIntegerDefaultValue(field.defaultValue);
        if (parsedDefaultValue !== undefined) {
          fieldSchema.default = parsedDefaultValue;
        }
      }
      applyNumberSchemaAdvancedOptions(fieldSchema, advancedOptions);
      break;
    case 'number':
      fieldSchema.type = 'number';
      if (hasCompatibleScalarEnumValues(field.enumValues, 'number')) {
        fieldSchema.enum = cloneEnumValues(field.enumValues);
      }
      if (field.defaultValue !== undefined && field.defaultValue !== '') {
        const parsedDefaultValue = parseNumberDefaultValue(field.defaultValue);
        if (parsedDefaultValue !== undefined) {
          fieldSchema.default = parsedDefaultValue;
        }
      }
      applyNumberSchemaAdvancedOptions(fieldSchema, advancedOptions);
      break;
    case 'boolean':
      fieldSchema.type = 'boolean';
      if (hasCompatibleScalarEnumValues(field.enumValues, 'boolean')) {
        fieldSchema.enum = cloneEnumValues(field.enumValues);
      }
      if (field.defaultValue !== undefined) {
        fieldSchema.default = Boolean(field.defaultValue);
      }
      break;
    case 'enum': {
      const enumValues = getStringEnumValues(field.enumValues);
      fieldSchema.type = 'string';
      fieldSchema.enum = enumValues;
      if (typeof field.defaultValue === 'string' && enumValues.includes(field.defaultValue)) {
        fieldSchema.default = field.defaultValue;
      }
      applyStringSchemaAdvancedOptions(fieldSchema, advancedOptions);
      break;
    }
    case 'object':
      Object.assign(
        fieldSchema,
        createObjectSchemaFromFields(
          field.nestedFields || [],
          field.allowAdditionalProperties,
        ),
      );
      if (field.defaultValue !== undefined) {
        fieldSchema.default = cloneSchemaFragment(field.defaultValue);
      }
      applyObjectSchemaAdvancedOptions(fieldSchema, advancedOptions);
      break;
    case 'array_string':
      fieldSchema.type = 'array';
      fieldSchema.items = { type: 'string' };
      if (hasCompatibleScalarEnumValues(field.enumValues, 'string')) {
        fieldSchema.items.enum = cloneEnumValues(field.enumValues);
      }
      if (field.defaultValue !== undefined) {
        fieldSchema.default = cloneSchemaFragment(field.defaultValue);
      }
      applyArrayItemSchemaAdvancedOptions(fieldSchema.items, field.type, advancedOptions);
      applyArraySchemaAdvancedOptions(fieldSchema, advancedOptions);
      break;
    case 'array_enum': {
      const enumValues = getStringEnumValues(field.enumValues);
      fieldSchema.type = 'array';
      fieldSchema.items = {
        type: 'string',
        enum: enumValues,
      };
      if (field.defaultValue !== undefined) {
        fieldSchema.default = cloneSchemaFragment(field.defaultValue);
      }
      applyArrayItemSchemaAdvancedOptions(fieldSchema.items, field.type, advancedOptions);
      applyArraySchemaAdvancedOptions(fieldSchema, advancedOptions);
      break;
    }
    case 'array_integer':
      fieldSchema.type = 'array';
      fieldSchema.items = { type: 'integer' };
      if (hasCompatibleScalarEnumValues(field.enumValues, 'integer')) {
        fieldSchema.items.enum = cloneEnumValues(field.enumValues);
      }
      if (field.defaultValue !== undefined) {
        fieldSchema.default = cloneSchemaFragment(field.defaultValue);
      }
      applyArrayItemSchemaAdvancedOptions(fieldSchema.items, field.type, advancedOptions);
      applyArraySchemaAdvancedOptions(fieldSchema, advancedOptions);
      break;
    case 'array_number':
      fieldSchema.type = 'array';
      fieldSchema.items = { type: 'number' };
      if (hasCompatibleScalarEnumValues(field.enumValues, 'number')) {
        fieldSchema.items.enum = cloneEnumValues(field.enumValues);
      }
      if (field.defaultValue !== undefined) {
        fieldSchema.default = cloneSchemaFragment(field.defaultValue);
      }
      applyArrayItemSchemaAdvancedOptions(fieldSchema.items, field.type, advancedOptions);
      applyArraySchemaAdvancedOptions(fieldSchema, advancedOptions);
      break;
    case 'array_boolean':
      fieldSchema.type = 'array';
      fieldSchema.items = { type: 'boolean' };
      if (hasCompatibleScalarEnumValues(field.enumValues, 'boolean')) {
        fieldSchema.items.enum = cloneEnumValues(field.enumValues);
      }
      if (field.defaultValue !== undefined) {
        fieldSchema.default = cloneSchemaFragment(field.defaultValue);
      }
      applyArraySchemaAdvancedOptions(fieldSchema, advancedOptions);
      break;
    case 'array_object':
      fieldSchema.type = 'array';
      fieldSchema.items = createObjectSchemaFromFields(
        field.nestedFields || [],
        field.allowAdditionalProperties,
      );
      if (field.defaultValue !== undefined) {
        fieldSchema.default = cloneSchemaFragment(field.defaultValue);
      }
      applyArrayItemSchemaAdvancedOptions(fieldSchema.items, field.type, advancedOptions);
      applyArraySchemaAdvancedOptions(fieldSchema, advancedOptions);
      break;
    default:
      fieldSchema.type = 'string';
      break;
  }

  return fieldSchema;
};

const isUneditedImplicitHiddenRequiredField = (
  field: PromptSchemaFieldDefinition,
): boolean => (
  (field.type === 'string' || field.type === 'any')
  && field.description === ''
  && field.defaultValue === undefined
  && (field.enumValues?.length || 0) === 0
  && (field.nestedFields?.length || 0) === 0
);

const shouldPreserveHiddenRequiredField = (
  field: PromptSchemaFieldDefinition,
  fieldSchema: Record<string, any>,
  allowAdditionalProperties?: PromptSchemaAdditionalProperties,
): boolean => {
  if (!field.preserveAsHiddenRequired) {
    return false;
  }

  if (allowAdditionalProperties === false) {
    return false;
  }

  if (field.sourceSchema) {
    return areJsonLikeValuesEqual(
      fieldSchema,
      projectBuilderManagedSchema(field.sourceSchema),
    );
  }

  return isUneditedImplicitHiddenRequiredField(field);
};

const mergeFieldSchemaWithSourceSchema = (
  fieldSchema: Record<string, any>,
  sourceSchema?: Record<string, any>,
): Record<string, any> => {
  if (!isPlainObject(fieldSchema) || !isPlainObject(sourceSchema)) {
    return fieldSchema;
  }

  if (getSchemaShapeKey(fieldSchema) !== getSchemaShapeKey(sourceSchema)) {
    return fieldSchema;
  }

  const mergedSchema = cloneSchemaFragment(sourceSchema);
  BUILDER_MANAGED_SCHEMA_KEYS.forEach((key) => {
    delete mergedSchema[key];
  });

  Object.entries(fieldSchema).forEach(([key, value]) => {
    if (key === 'properties' && isPlainObject(value) && isPlainObject(sourceSchema.properties)) {
      mergedSchema.properties = Object.entries(value).reduce<Record<string, any>>(
        (nextProperties, [propertyName, propertySchema]) => {
          nextProperties[propertyName] = mergeFieldSchemaWithSourceSchema(
            propertySchema as Record<string, any>,
            isPlainObject(sourceSchema.properties[propertyName])
              ? sourceSchema.properties[propertyName]
              : undefined,
          );
          return nextProperties;
        },
        {},
      );
      return;
    }

    if (key === 'items' && isPlainObject(value) && isPlainObject(sourceSchema.items)) {
      mergedSchema.items = mergeFieldSchemaWithSourceSchema(
        value as Record<string, any>,
        sourceSchema.items,
      );
      return;
    }

    if (key === 'additionalProperties' && isPlainObject(value) && isPlainObject(sourceSchema.additionalProperties)) {
      mergedSchema.additionalProperties = mergeFieldSchemaWithSourceSchema(
        value as Record<string, any>,
        sourceSchema.additionalProperties,
      );
      return;
    }

    mergedSchema[key] = cloneSchemaFragment(value);
  });

  return mergedSchema;
};

const createObjectSchemaFromFields = (
  fields: PromptSchemaFieldDefinition[],
  allowAdditionalProperties?: PromptSchemaAdditionalProperties,
): Record<string, any> => {
  const properties: Record<string, any> = {};
  const required: string[] = [];

  fields.forEach((field) => {
    const fieldName = field.name.trim();
    if (!fieldName) {
      return;
    }

    const fieldSchema = buildFieldSchemaFragment(field);
    const preserveAsHiddenRequired = shouldPreserveHiddenRequiredField(
      field,
      fieldSchema,
      allowAdditionalProperties,
    );

    if (!preserveAsHiddenRequired) {
      properties[fieldName] = mergeFieldSchemaWithSourceSchema(fieldSchema, field.sourceSchema);
    }

    if (field.required) {
      required.push(fieldName);
    }
  });

  const objectSchema: Record<string, any> = {
    type: 'object',
    properties,
  };

  if (required.length > 0) {
    objectSchema.required = required;
  }

  if (allowAdditionalProperties !== undefined) {
    if (allowAdditionalProperties === IMPLICIT_OPEN_ADDITIONAL_PROPERTIES) {
      return objectSchema;
    }

    objectSchema.additionalProperties = clonePromptSchemaAdditionalProperties(allowAdditionalProperties);
  }

  return objectSchema;
};

const collectFieldAdvancedOptions = (
  schema: Record<string, any>,
  type: PromptSchemaFieldType,
): PromptSchemaFieldAdvancedOptions | undefined => {
  const nextOptions: PromptSchemaFieldAdvancedOptions = {};

  if (type === 'string' || type === 'enum') {
    if (hasOwn(schema, 'minLength')) {
      nextOptions.minLength = schema.minLength;
    }
    if (hasOwn(schema, 'maxLength')) {
      nextOptions.maxLength = schema.maxLength;
    }
    if (typeof schema.pattern === 'string') {
      nextOptions.pattern = schema.pattern;
    }
    if (typeof schema.format === 'string') {
      nextOptions.format = schema.format;
    }
  }

  if (type === 'integer' || type === 'number') {
    if (hasOwn(schema, 'minimum')) {
      nextOptions.minimum = schema.minimum;
    }
    if (hasOwn(schema, 'maximum')) {
      nextOptions.maximum = schema.maximum;
    }
  }

  if (type === 'object') {
    if (hasOwn(schema, 'minProperties')) {
      nextOptions.minProperties = schema.minProperties;
    }
    if (hasOwn(schema, 'maxProperties')) {
      nextOptions.maxProperties = schema.maxProperties;
    }
  }

  if (type.startsWith('array_')) {
    if (hasOwn(schema, 'minItems')) {
      nextOptions.minItems = schema.minItems;
    }
    if (hasOwn(schema, 'maxItems')) {
      nextOptions.maxItems = schema.maxItems;
    }
    if (schema.uniqueItems === true) {
      nextOptions.uniqueItems = true;
    }

    if (isPlainObject(schema.items)) {
      const itemAdvancedOptions = type === 'array_string' || type === 'array_enum'
        ? collectFieldAdvancedOptions(schema.items, type === 'array_enum' ? 'enum' : 'string')
        : type === 'array_integer'
          ? collectFieldAdvancedOptions(schema.items, 'integer')
          : type === 'array_number'
            ? collectFieldAdvancedOptions(schema.items, 'number')
            : type === 'array_object'
              ? collectFieldAdvancedOptions(schema.items, 'object')
              : undefined;

      Object.assign(nextOptions, itemAdvancedOptions);
    }
  }

  return compactPromptSchemaFieldAdvancedOptions(nextOptions);
};

export const createPromptSchemaField = (
  overrides: Partial<PromptSchemaFieldDefinition> = {},
): PromptSchemaFieldDefinition => {
  const baseField: PromptSchemaFieldDefinition = {
    id: createFieldId(),
    name: '',
    type: 'string',
    description: '',
    required: false,
    allowAdditionalProperties: false,
    defaultValue: undefined,
    enumValues: [],
    nestedFields: [],
  };

  return {
    ...baseField,
    ...overrides,
    allowAdditionalProperties: hasOwn(overrides, 'allowAdditionalProperties')
      ? clonePromptSchemaAdditionalProperties(overrides.allowAdditionalProperties)
      : baseField.allowAdditionalProperties,
    enumValues: cloneEnumValues(overrides.enumValues),
    advancedOptions: clonePromptSchemaFieldAdvancedOptions(
      compactPromptSchemaFieldAdvancedOptions(overrides.advancedOptions),
    ),
    nestedFields: [...(overrides.nestedFields || [])],
    preserveAsHiddenRequired: overrides.preserveAsHiddenRequired,
    sourceSchema: cloneSourceSchema(overrides.sourceSchema),
  };
};

const schemaEntryToPromptSchemaField = (
  key: string,
  value: any,
  required: boolean,
): PromptSchemaFieldDefinition => {
  let type: PromptSchemaFieldType = value?.type === undefined ? 'any' : 'string';
  let enumValues: PromptSchemaEnumValue[] = [];
  let nestedFields: PromptSchemaFieldDefinition[] = [];

  if (value?.type === 'string' && Array.isArray(value?.enum) && areAllStringEnumValues(value.enum)) {
    type = 'enum';
    enumValues = cloneEnumValues(value.enum);
  } else if (value?.type === 'string' && Array.isArray(value?.enum)) {
    type = 'string';
    enumValues = cloneEnumValues(value.enum);
  } else if (value?.type === 'object') {
    type = 'object';
    nestedFields = schemaToPromptSchemaFields(value);
  } else if (
    value?.type === 'array'
    && value?.items?.type === 'string'
    && Array.isArray(value?.items?.enum)
    && areAllStringEnumValues(value.items.enum)
  ) {
    type = 'array_enum';
    enumValues = cloneEnumValues(value.items.enum);
  } else if (value?.type === 'array' && value?.items?.type === 'string') {
    type = 'array_string';
    enumValues = Array.isArray(value?.items?.enum) ? cloneEnumValues(value.items.enum) : [];
  } else if (value?.type === 'array' && value?.items?.type === 'integer') {
    type = 'array_integer';
    enumValues = Array.isArray(value?.items?.enum) ? cloneEnumValues(value.items.enum) : [];
  } else if (value?.type === 'array' && value?.items?.type === 'number') {
    type = 'array_number';
    enumValues = Array.isArray(value?.items?.enum) ? cloneEnumValues(value.items.enum) : [];
  } else if (value?.type === 'array' && value?.items?.type === 'boolean') {
    type = 'array_boolean';
    enumValues = Array.isArray(value?.items?.enum) ? cloneEnumValues(value.items.enum) : [];
  } else if (value?.type === 'array' && value?.items?.type === 'object') {
    type = 'array_object';
    nestedFields = schemaToPromptSchemaFields(value.items);
  } else if (value?.type === 'integer') {
    type = 'integer';
    enumValues = Array.isArray(value?.enum) ? cloneEnumValues(value.enum) : [];
  } else if (value?.type === 'number') {
    type = 'number';
    enumValues = Array.isArray(value?.enum) ? cloneEnumValues(value.enum) : [];
  } else if (value?.type === 'boolean') {
    type = 'boolean';
    enumValues = Array.isArray(value?.enum) ? cloneEnumValues(value.enum) : [];
  }

  return createPromptSchemaField({
    name: key,
    type,
    description: value?.description ?? '',
    required,
    allowAdditionalProperties: type === 'object'
      ? getJsonSchemaAllowsAdditionalProperties(value)
      : type === 'array_object'
        ? getJsonSchemaAllowsAdditionalProperties(value?.items)
        : false,
    defaultValue: value?.default,
    enumValues,
    advancedOptions: collectFieldAdvancedOptions(value ?? {}, type),
    nestedFields,
  });
};

const getHiddenRequiredNames = (schema: any): string[] => {
  if (!isDraft7OpenObjectSchema(schema)) {
    return [];
  }

  const visiblePropertyNames = new Set(
    schema?.properties && typeof schema.properties === 'object'
      ? Object.keys(schema.properties)
      : [],
  );

  return Array.isArray(schema?.required)
    ? schema.required.filter(
      (value: unknown): value is string => typeof value === 'string' && !visiblePropertyNames.has(value),
    )
    : [];
};

const createHiddenRequiredDynamicField = (
  key: string,
  additionalPropertiesSchema?: Record<string, any>,
): PromptSchemaFieldDefinition => createPromptSchemaField({
  ...schemaEntryToPromptSchemaField(
    key,
    additionalPropertiesSchema ?? {},
    true,
  ),
  preserveAsHiddenRequired: true,
  sourceSchema: cloneSourceSchema(additionalPropertiesSchema ?? {}),
});

export const schemaToPromptSchemaFields = (schema: any): PromptSchemaFieldDefinition[] => {
  if (!isPlainObject(schema)) {
    return [];
  }

  const hiddenRequiredNames = getHiddenRequiredNames(schema);
  const typedAdditionalProperties = isPlainObject(schema.additionalProperties)
    ? schema.additionalProperties
    : undefined;
  const schemaProperties = isPlainObject(schema.properties) ? schema.properties : {};

  return [
    ...Object.entries(schemaProperties).map(([key, rawValue]) => (
      schemaEntryToPromptSchemaField(
        key,
        rawValue,
        Array.isArray(schema.required) ? schema.required.includes(key) : false,
      )
    )),
    ...hiddenRequiredNames.map((key: string) => (
      createHiddenRequiredDynamicField(key, typedAdditionalProperties)
    )),
  ];
};

export const promptSchemaFieldsToJsonSchema = (
  fields: PromptSchemaFieldDefinition[],
  options: { allowAdditionalProperties?: PromptSchemaAdditionalProperties } = {},
): Record<string, any> => createObjectSchemaFromFields(
  fields,
  options.allowAdditionalProperties,
);

const isMatchingScalarEnumValue = (
  value: unknown,
  schemaType: 'string' | 'integer' | 'number' | 'boolean',
): boolean => {
  switch (schemaType) {
    case 'string':
      return typeof value === 'string';
    case 'integer':
      return typeof value === 'number' && Number.isInteger(value);
    case 'number':
      return typeof value === 'number' && Number.isFinite(value);
    case 'boolean':
      return typeof value === 'boolean';
    default:
      return false;
  }
};

const validateSchemaDefault = (schema: Record<string, any>, fieldPath: string): string | null => {
  if (!hasOwn(schema, 'default')) {
    return null;
  }

  const defaultValue = schema.default;
  if (Array.isArray(schema.enum) && schema.enum.length > 0) {
    const enumValues = schema.enum as PromptSchemaEnumValue[];
    if (!enumValues.some((enumValue) => areJsonLikeValuesEqual(enumValue, defaultValue))) {
      return `${fieldPath} default must match one of the enum values`;
    }
  }

  switch (schema.type) {
    case 'string':
      if (typeof defaultValue !== 'string') {
        return `${fieldPath} default must be text`;
      }
      break;
    case 'integer':
      if (!(typeof defaultValue === 'number' && Number.isInteger(defaultValue))) {
        return `${fieldPath} default must be an integer`;
      }
      break;
    case 'number':
      if (!(typeof defaultValue === 'number' && Number.isFinite(defaultValue))) {
        return `${fieldPath} default must be a number`;
      }
      break;
    case 'boolean':
      if (typeof defaultValue !== 'boolean') {
        return `${fieldPath} default must be true or false`;
      }
      break;
    case 'object':
      if (!isPlainObject(defaultValue)) {
        return `${fieldPath} default must be an object`;
      }
      {
        const objectValidationError = validateJsonSchemaObjectValue(
          schema,
          defaultValue,
          `${fieldPath} default`,
        );
        if (objectValidationError) {
          return objectValidationError;
        }
      }
      break;
    case 'array':
      if (!Array.isArray(defaultValue)) {
        return `${fieldPath} default must be an array`;
      }

      if (isPlainObject(schema.items)) {
        for (let index = 0; index < defaultValue.length; index += 1) {
          const itemValidationError = validateSchemaValueAgainstSchema(
            defaultValue[index],
            schema.items,
            `${fieldPath} default item ${index + 1}`,
          );
          if (itemValidationError) {
            return itemValidationError;
          }
        }
      }
      break;
    default:
      break;
  }

  return validateDraft7SchemaValue(schema, defaultValue, `${fieldPath} default`);
};

const validateJsonSchemaObjectValue = (
  schema: Record<string, any>,
  objectValue: Record<string, any>,
  schemaName: string,
): string | null => {
  const properties = hasOwn(schema, 'properties') && isPlainObject(schema.properties)
    ? schema.properties
    : {};
  const requiredEntries = Array.isArray(schema.required) ? schema.required : [];

  for (const requiredName of requiredEntries) {
    if (!hasOwn(objectValue, requiredName)) {
      return `${schemaName} must include '${requiredName}'`;
    }
  }

  for (const [propertyName, propertyValue] of Object.entries(objectValue)) {
    if (hasOwn(properties, propertyName)) {
      const propertyValidationError = validateSchemaValueAgainstSchema(
        propertyValue,
        properties[propertyName],
        `${schemaName}.${propertyName}`,
      );
      if (propertyValidationError) {
        return propertyValidationError;
      }
      continue;
    }

    if (schema.additionalProperties === false) {
      return `${schemaName} does not allow additional property '${propertyName}'`;
    }

    if (isPlainObject(schema.additionalProperties)) {
      const additionalPropertyValidationError = validateSchemaValueAgainstSchema(
        propertyValue,
        schema.additionalProperties,
        `${schemaName}.${propertyName}`,
      );
      if (additionalPropertyValidationError) {
        return additionalPropertyValidationError;
      }
    }
  }

  return null;
};

const validateSchemaValueAgainstSchema = (
  value: unknown,
  schema: Record<string, any>,
  fieldPath: string,
): string | null => {
  if (Array.isArray(schema.enum) && schema.enum.length > 0) {
    const enumValues = schema.enum as PromptSchemaEnumValue[];
    if (!enumValues.some((enumValue) => areJsonLikeValuesEqual(enumValue, value))) {
      return `${fieldPath} must match one of the enum values`;
    }
  }

  switch (schema.type) {
    case 'string':
      return typeof value === 'string' ? null : `${fieldPath} must be text`;
    case 'integer':
      return typeof value === 'number' && Number.isInteger(value)
        ? null
        : `${fieldPath} must be an integer`;
    case 'number':
      return typeof value === 'number' && Number.isFinite(value)
        ? null
        : `${fieldPath} must be a number`;
    case 'boolean':
      return typeof value === 'boolean' ? null : `${fieldPath} must be true or false`;
    case 'object':
      return validateJsonSchemaObject(schema, fieldPath, false, value);
    case 'array':
      if (!Array.isArray(value)) {
        return `${fieldPath} must be an array`;
      }
      if (isPlainObject(schema.items)) {
        for (let index = 0; index < value.length; index += 1) {
          const itemValidationError = validateSchemaValueAgainstSchema(
            value[index],
            schema.items,
            `${fieldPath}[${index}]`,
          );
          if (itemValidationError) {
            return itemValidationError;
          }
        }
      }
      return null;
    default:
      return null;
  }
};

const validateJsonSchemaProperty = (
  propertyPath: string,
  propertySchema: Record<string, any>,
  options: { allowEmptyObjects?: boolean } = {},
): string | null => {
  const nestedOptions = options.allowEmptyObjects
    ? { ...options, allowEmptyObjects: false }
    : options;

  if (!isPlainObject(propertySchema)) {
    return `${propertyPath} must be a JSON object`;
  }

  if (!hasOwn(propertySchema, 'type')) {
    const unsupportedUntypedKeywords = ['properties', 'required', 'additionalProperties', 'items', 'enum'];
    const invalidUntypedKeyword = unsupportedUntypedKeywords.find((keyword) => hasOwn(propertySchema, keyword));
    return invalidUntypedKeyword
      ? `${propertyPath} must declare a type when using ${invalidUntypedKeyword}`
      : null;
  }

  const propertyType = propertySchema.type;
  const supportedTypes = ['string', 'integer', 'number', 'boolean', 'object', 'array'];
  if (typeof propertyType !== 'string' || !supportedTypes.includes(propertyType)) {
    return `${propertyPath} has unsupported type '${String(propertyType)}'`;
  }

  if (Array.isArray(propertySchema.enum)) {
    if (propertySchema.enum.length === 0) {
      return `${propertyPath} enum must contain at least one value`;
    }

    const enumSeenValues = new Set<string>();
    for (const enumValue of propertySchema.enum as unknown[]) {
      if (!['string', 'integer', 'number', 'boolean'].includes(propertyType)) {
        return `${propertyPath} enum is only supported for scalar fields`;
      }
      if (!isMatchingScalarEnumValue(
        enumValue,
        propertyType as 'string' | 'integer' | 'number' | 'boolean',
      )) {
        return `${propertyPath} enum values must match type '${propertyType}'`;
      }
      const normalizedEnumValue = JSON.stringify(normalizeJsonLikeValue(enumValue));
      if (enumSeenValues.has(normalizedEnumValue)) {
        return `${propertyPath} enum values must be unique`;
      }
      enumSeenValues.add(normalizedEnumValue);
    }
  }

  if (propertyType === 'object') {
    return validateJsonSchemaObject(propertySchema, propertyPath, false, undefined, nestedOptions);
  }

  if (propertyType === 'array') {
    if (!isPlainObject(propertySchema.items)) {
      return `${propertyPath} must define an items schema`;
    }
    if (!hasOwn(propertySchema.items, 'type')) {
      return `${propertyPath}[] must declare a type`;
    }
    if (propertySchema.items.type === 'array') {
      return `${propertyPath}[] has unsupported type 'array'`;
    }
    const itemSchemaError = validateJsonSchemaProperty(
      `${propertyPath}[]`,
      propertySchema.items,
      nestedOptions,
    );
    if (itemSchemaError) {
      return itemSchemaError;
    }
  }

  return validateSchemaDefault(propertySchema, propertyPath);
};

const validateJsonSchemaObject = (
  schema: Record<string, any>,
  schemaName: string,
  requireProperties = false,
  objectValue?: unknown,
  options: { allowEmptyObjects?: boolean } = {},
): string | null => {
  if (!isPlainObject(schema)) {
    return `${schemaName} must be a JSON object`;
  }

  if (schema.type !== 'object') {
    return `${schemaName} must have type 'object'`;
  }

  const properties = hasOwn(schema, 'properties') ? schema.properties : {};
  if (!isPlainObject(properties)) {
    return `${schemaName} properties must be an object`;
  }

  const allowsOpenObject = schema.additionalProperties !== false;
  if (!options.allowEmptyObjects && (requireProperties || !allowsOpenObject) && Object.keys(properties).length === 0) {
    return `${schemaName} must define at least one property`;
  }

  const requiredEntries = hasOwn(schema, 'required') ? schema.required : [];
  if (requiredEntries !== undefined && !Array.isArray(requiredEntries)) {
    return `${schemaName} required must be an array`;
  }

  const requiredFieldNames = Array.isArray(requiredEntries)
    ? requiredEntries
    : [];
  for (const requiredName of requiredFieldNames) {
    if (typeof requiredName !== 'string' || !requiredName) {
      return `${schemaName} required entries must be non-empty strings`;
    }
    const requiredFieldNameError = getPromptSchemaFieldNameValidationError(requiredName, schemaName);
    if (requiredFieldNameError) {
      return requiredFieldNameError;
    }
    if (!allowsOpenObject && !hasOwn(properties, requiredName)) {
      return `${schemaName} required field '${requiredName}' must exist in properties`;
    }
  }

  for (const [propertyName, propertySchema] of Object.entries(properties)) {
    const propertyFieldNameError = getPromptSchemaFieldNameValidationError(propertyName, schemaName);
    if (propertyFieldNameError) {
      return propertyFieldNameError;
    }
    const propertyValidationError = validateJsonSchemaProperty(
      `${schemaName}.${propertyName}`,
      propertySchema as Record<string, any>,
      options,
    );
    if (propertyValidationError) {
      return propertyValidationError;
    }
  }

  if (isPlainObject(schema.additionalProperties)) {
    const additionalPropertiesValidationError = validateJsonSchemaProperty(
      `${schemaName}.*`,
      schema.additionalProperties,
      options,
    );
    if (additionalPropertiesValidationError) {
      return additionalPropertiesValidationError;
    }
  }

  if (objectValue !== undefined) {
    if (!isPlainObject(objectValue)) {
      return `${schemaName} must be an object`;
    }
    const objectValueValidationError = validateJsonSchemaObjectValue(
      schema,
      objectValue,
      schemaName,
    );
    if (objectValueValidationError) {
      return objectValueValidationError;
    }
  }

  return validateSchemaDefault(schema, schemaName);
};

export const validatePromptJsonSchema = (
  schema: Record<string, any>,
  options: { allowEmptyObjects?: boolean } = {},
): string | null => {
  const isValidDraft7Schema = promptSchemaDraft7Validator.validateSchema(schema);
  if (!isValidDraft7Schema) {
    return formatDraft7SchemaValidationError(promptSchemaDraft7Validator.errors);
  }

  return validateJsonSchemaObject(schema, 'Schema', false, undefined, options);
};

export const explicitizeImplicitOpenFieldDefinitions = (
  fields: PromptSchemaFieldDefinition[],
): PromptSchemaFieldDefinition[] => fields.map((field) => {
  const nextNestedFields = field.nestedFields
    ? explicitizeImplicitOpenFieldDefinitions(field.nestedFields)
    : field.nestedFields;
  const shouldExplicitizeOpenObject = isObjectFieldType(field.type) && field.allowAdditionalProperties === undefined;

  return {
    ...field,
    allowAdditionalProperties: shouldExplicitizeOpenObject
      ? IMPLICIT_OPEN_ADDITIONAL_PROPERTIES
      : field.allowAdditionalProperties,
    nestedFields: nextNestedFields,
  };
});

export const schemaToBuilderPromptSchemaFields = (
  schema: any,
): PromptSchemaFieldDefinition[] => explicitizeImplicitOpenFieldDefinitions(
  schemaToPromptSchemaFields(schema),
);

const isDraft7OpenObjectSchema = (schema: unknown): schema is Record<string, any> => (
  isPlainObject(schema)
  && schema.type === 'object'
  && schema.additionalProperties !== false
);

const copyUnsupportedSchemaKeywords = (
  nextSchema: Record<string, any>,
  originalSchema: Record<string, any>,
): void => {
  Object.entries(originalSchema).forEach(([key, value]) => {
    if (BUILDER_MANAGED_SCHEMA_KEYS.has(key)) {
      return;
    }

    nextSchema[key] = cloneSchemaFragment(value);
  });
};

const mergeHiddenRequiredKeys = (
  nextSchema: Record<string, any>,
  originalSchema: Record<string, any>,
): void => {
  if (!isDraft7OpenObjectSchema(nextSchema)) {
    return;
  }

  const nextPropertyNames = new Set(
    isPlainObject(nextSchema.properties) ? Object.keys(nextSchema.properties) : [],
  );
  const originalPropertyNames = new Set(
    isPlainObject(originalSchema.properties) ? Object.keys(originalSchema.properties) : [],
  );
  const surfacedHiddenRequiredNames = new Set(getHiddenRequiredNames(originalSchema));
  const hiddenRequiredNames = Array.isArray(originalSchema.required)
    ? originalSchema.required.filter(
      (value): value is string => (
        typeof value === 'string'
        && !originalPropertyNames.has(value)
        && !nextPropertyNames.has(value)
        && !surfacedHiddenRequiredNames.has(value)
      ),
    )
    : [];

  if (hiddenRequiredNames.length === 0) {
    return;
  }

  const visibleRequiredNames = Array.isArray(nextSchema.required)
    ? nextSchema.required.filter((value): value is string => typeof value === 'string')
    : [];

  nextSchema.required = Array.from(new Set([...visibleRequiredNames, ...hiddenRequiredNames]));
};

export const mergePromptSchemaWithOriginal = (
  nextSchema: Record<string, any>,
  originalSchema?: Record<string, any> | null,
): Record<string, any> => {
  if (!isPlainObject(nextSchema) || !isPlainObject(originalSchema)) {
    return nextSchema;
  }

  if (nextSchema.type !== originalSchema.type) {
    return nextSchema;
  }

  const mergedSchema = cloneSchemaFragment(nextSchema);
  copyUnsupportedSchemaKeywords(mergedSchema, originalSchema);

  if (mergedSchema.type === 'object') {
    if (isPlainObject(mergedSchema.properties) && isPlainObject(originalSchema.properties)) {
      Object.entries(mergedSchema.properties).forEach(([propertyName, propertySchema]) => {
        if (!isPlainObject(propertySchema) || !isPlainObject(originalSchema.properties[propertyName])) {
          return;
        }

        mergedSchema.properties[propertyName] = mergePromptSchemaWithOriginal(
          propertySchema,
          originalSchema.properties[propertyName],
        );
      });
    }

    mergeHiddenRequiredKeys(mergedSchema, originalSchema);
    return mergedSchema;
  }

  if (mergedSchema.type === 'array' && isPlainObject(mergedSchema.items) && isPlainObject(originalSchema.items)) {
    if (mergedSchema.items.type === originalSchema.items.type) {
      mergedSchema.items = mergePromptSchemaWithOriginal(mergedSchema.items, originalSchema.items);
    }
  }

  return mergedSchema;
};

export const getJsonSchemaAllowsAdditionalProperties = (
  schema: any,
): PromptSchemaAdditionalProperties => {
  if (!schema || !hasOwn(schema, 'additionalProperties')) {
    return undefined;
  }

  return clonePromptSchemaAdditionalProperties(schema.additionalProperties);
};

export const getBuilderSchemaAllowsAdditionalProperties = (
  schema: any,
): PromptSchemaAdditionalProperties => {
  if (!isPlainObject(schema) || schema.type !== 'object') {
    return false;
  }

  return getJsonSchemaAllowsAdditionalProperties(schema) ?? IMPLICIT_OPEN_ADDITIONAL_PROPERTIES;
};

export const isAdditionalPropertiesEnabled = (
  value: PromptSchemaAdditionalProperties,
): boolean => value !== undefined && value !== false;

export const getPromptSchemaFieldTypeLabel = (type: PromptSchemaFieldType): string => {
  switch (type) {
    case 'any':
      return 'Any Value';
    case 'string':
      return 'Text';
    case 'integer':
      return 'Integer';
    case 'number':
      return 'Number';
    case 'boolean':
      return 'True/False';
    case 'enum':
      return 'Enum';
    case 'object':
      return 'Dictionary';
    case 'array_string':
      return 'List of Text';
    case 'array_enum':
      return 'List of Enum';
    case 'array_integer':
      return 'List of Integers';
    case 'array_number':
      return 'List of Numbers';
    case 'array_boolean':
      return 'List of True/False';
    case 'array_object':
      return 'List of Dicts';
    default:
      return type;
  }
};

export const getPromptSchemaFieldTypeCompactLabel = (type: PromptSchemaFieldType): string => {
  switch (type) {
    case 'any':
      return 'any';
    case 'string':
      return 'text';
    case 'integer':
      return 'int';
    case 'number':
      return 'num';
    case 'boolean':
      return 'bool';
    case 'enum':
      return 'enum';
    case 'object':
      return 'dict';
    case 'array_string':
      return 'list';
    case 'array_enum':
      return 'enum[]';
    case 'array_integer':
      return 'int[]';
    case 'array_number':
      return 'num[]';
    case 'array_boolean':
      return 'bool[]';
    case 'array_object':
      return 'dict[]';
    default:
      return type;
  }
};

export const getPromptSchemaFieldStyleCategory = (
  type: PromptSchemaFieldType,
): PromptSchemaFieldStyleCategory => {
  switch (type) {
    case 'string':
    case 'enum':
    case 'any':
      return 'string';
    case 'integer':
    case 'number':
      return 'number';
    case 'boolean':
      return 'boolean';
    case 'array_string':
    case 'array_enum':
    case 'array_integer':
    case 'array_number':
    case 'array_boolean':
    case 'array_object':
      return 'array';
    case 'object':
      return 'object';
    default:
      return 'default';
  }
};

export const getPromptSchemaDisplayFields = (schema: any): PromptSchemaDisplayField[] => (
  schemaToPromptSchemaFields(schema).map((field) => ({
    name: field.name,
    description: field.description,
    required: field.required,
    type: field.type,
    typeLabel: getPromptSchemaFieldTypeLabel(field.type),
    compactTypeLabel: getPromptSchemaFieldTypeCompactLabel(field.type),
    styleCategory: getPromptSchemaFieldStyleCategory(field.type),
  }))
);

const fieldDefinitionToDisplayFieldWithNesting = (
  field: PromptSchemaFieldDefinition,
  depth: number,
): PromptSchemaDisplayFieldWithNesting => ({
  name: field.name,
  description: field.description,
  required: field.required,
  type: field.type,
  typeLabel: getPromptSchemaFieldTypeLabel(field.type),
  compactTypeLabel: getPromptSchemaFieldTypeCompactLabel(field.type),
  styleCategory: getPromptSchemaFieldStyleCategory(field.type),
  depth,
  nestedFields: field.nestedFields && field.nestedFields.length > 0
    ? field.nestedFields.map((nestedField) => (
        fieldDefinitionToDisplayFieldWithNesting(nestedField, depth + 1)
      ))
    : undefined,
});

export const getPromptSchemaDisplayFieldsWithNesting = (
  schema: any,
): PromptSchemaDisplayFieldWithNesting[] => (
  schemaToPromptSchemaFields(schema).map((field) => (
    fieldDefinitionToDisplayFieldWithNesting(field, 0)
  ))
);

export const getDuplicatePromptSchemaFieldNames = (
  fields: PromptSchemaFieldDefinition[],
): string[] => {
  const normalizedNames = fields
    .map((field) => normalizePromptSchemaFieldName(field.name))
    .filter(Boolean);

  return Array.from(
    new Set(
      normalizedNames.filter((name, index) => normalizedNames.indexOf(name) !== index),
    ),
  );
};

const getAdvancedOptionNonNegativeIntegerError = (
  value: unknown,
  label: string,
): string | null => {
  if (value === undefined || value === '') {
    return null;
  }

  return parseNonNegativeIntegerOption(value) === undefined
    ? `${label} must be a non-negative whole number`
    : null;
};

const getAdvancedOptionFiniteNumberError = (
  value: unknown,
  label: string,
): string | null => {
  if (value === undefined || value === '') {
    return null;
  }

  return parseFiniteNumberOption(value) === undefined
    ? `${label} must be a valid number`
    : null;
};

const getStringAdvancedOptionsValidationError = (
  advancedOptions: PromptSchemaFieldAdvancedOptions,
  fieldPrefix: string,
): string | null => {
  const minLengthError = getAdvancedOptionNonNegativeIntegerError(
    advancedOptions.minLength,
    `${fieldPrefix} min length`,
  );
  if (minLengthError) {
    return minLengthError;
  }

  const maxLengthError = getAdvancedOptionNonNegativeIntegerError(
    advancedOptions.maxLength,
    `${fieldPrefix} max length`,
  );
  if (maxLengthError) {
    return maxLengthError;
  }

  const minLength = parseNonNegativeIntegerOption(advancedOptions.minLength);
  const maxLength = parseNonNegativeIntegerOption(advancedOptions.maxLength);
  if (minLength !== undefined && maxLength !== undefined && maxLength < minLength) {
    return `${fieldPrefix} max length must be greater than or equal to min length`;
  }

  if (typeof advancedOptions.pattern === 'string' && advancedOptions.pattern !== '') {
    try {
      // Validate regex syntax before building the JSON schema.
      // eslint-disable-next-line no-new
      new RegExp(advancedOptions.pattern);
    } catch (error) {
      return `${fieldPrefix} pattern must be a valid regular expression`;
    }
  }

  return null;
};

const getNumberAdvancedOptionsValidationError = (
  advancedOptions: PromptSchemaFieldAdvancedOptions,
  fieldPrefix: string,
): string | null => {
  const minimumError = getAdvancedOptionFiniteNumberError(
    advancedOptions.minimum,
    `${fieldPrefix} minimum`,
  );
  if (minimumError) {
    return minimumError;
  }

  const maximumError = getAdvancedOptionFiniteNumberError(
    advancedOptions.maximum,
    `${fieldPrefix} maximum`,
  );
  if (maximumError) {
    return maximumError;
  }

  const minimum = parseFiniteNumberOption(advancedOptions.minimum);
  const maximum = parseFiniteNumberOption(advancedOptions.maximum);
  if (minimum !== undefined && maximum !== undefined && maximum < minimum) {
    return `${fieldPrefix} maximum must be greater than or equal to minimum`;
  }

  return null;
};

const getArrayAdvancedOptionsValidationError = (
  advancedOptions: PromptSchemaFieldAdvancedOptions,
  fieldPrefix: string,
): string | null => {
  const minItemsError = getAdvancedOptionNonNegativeIntegerError(
    advancedOptions.minItems,
    `${fieldPrefix} min items`,
  );
  if (minItemsError) {
    return minItemsError;
  }

  const maxItemsError = getAdvancedOptionNonNegativeIntegerError(
    advancedOptions.maxItems,
    `${fieldPrefix} max items`,
  );
  if (maxItemsError) {
    return maxItemsError;
  }

  const minItems = parseNonNegativeIntegerOption(advancedOptions.minItems);
  const maxItems = parseNonNegativeIntegerOption(advancedOptions.maxItems);
  if (minItems !== undefined && maxItems !== undefined && maxItems < minItems) {
    return `${fieldPrefix} max items must be greater than or equal to min items`;
  }

  return null;
};

const getObjectAdvancedOptionsValidationError = (
  advancedOptions: PromptSchemaFieldAdvancedOptions,
  fieldPrefix: string,
): string | null => {
  const minPropertiesError = getAdvancedOptionNonNegativeIntegerError(
    advancedOptions.minProperties,
    `${fieldPrefix} min properties`,
  );
  if (minPropertiesError) {
    return minPropertiesError;
  }

  const maxPropertiesError = getAdvancedOptionNonNegativeIntegerError(
    advancedOptions.maxProperties,
    `${fieldPrefix} max properties`,
  );
  if (maxPropertiesError) {
    return maxPropertiesError;
  }

  const minProperties = parseNonNegativeIntegerOption(advancedOptions.minProperties);
  const maxProperties = parseNonNegativeIntegerOption(advancedOptions.maxProperties);
  if (minProperties !== undefined && maxProperties !== undefined && maxProperties < minProperties) {
    return `${fieldPrefix} max properties must be greater than or equal to min properties`;
  }

  return null;
};

const getPromptSchemaFieldAdvancedOptionsValidationError = (
  field: PromptSchemaFieldDefinition,
): string | null => {
  const advancedOptions = compactPromptSchemaFieldAdvancedOptions(field.advancedOptions);
  if (!advancedOptions) {
    return null;
  }

  const fieldPrefix = `Field "${field.name}"`;

  if (
    field.type === 'string'
    || field.type === 'enum'
    || field.type === 'array_string'
    || field.type === 'array_enum'
  ) {
    const stringOptionsError = getStringAdvancedOptionsValidationError(
      advancedOptions,
      field.type.startsWith('array_') ? `${fieldPrefix} item` : fieldPrefix,
    );
    if (stringOptionsError) {
      return stringOptionsError;
    }
  }

  if (
    field.type === 'integer'
    || field.type === 'number'
    || field.type === 'array_integer'
    || field.type === 'array_number'
  ) {
    const numberOptionsError = getNumberAdvancedOptionsValidationError(
      advancedOptions,
      field.type.startsWith('array_') ? `${fieldPrefix} item` : fieldPrefix,
    );
    if (numberOptionsError) {
      return numberOptionsError;
    }
  }

  if (field.type.startsWith('array_')) {
    const arrayOptionsError = getArrayAdvancedOptionsValidationError(advancedOptions, fieldPrefix);
    if (arrayOptionsError) {
      return arrayOptionsError;
    }
  }

  if (field.type === 'object' || field.type === 'array_object') {
    const objectOptionsError = getObjectAdvancedOptionsValidationError(
      advancedOptions,
      field.type === 'array_object' ? `${fieldPrefix} item` : fieldPrefix,
    );
    if (objectOptionsError) {
      return objectOptionsError;
    }
  }

  return null;
};

type PromptSchemaFieldDefaultValidationValue =
  | { hasDefault: false }
  | { hasDefault: true; value: unknown };

const getPromptSchemaFieldDefaultValueForValidation = (
  field: PromptSchemaFieldDefinition,
): PromptSchemaFieldDefaultValidationValue => {
  if (field.defaultValue === undefined) {
    return { hasDefault: false };
  }

  if (field.type === 'integer') {
    if (field.defaultValue === '') {
      return { hasDefault: false };
    }

    const parsedDefaultValue = parseIntegerDefaultValue(field.defaultValue);
    return {
      hasDefault: true,
      value: parsedDefaultValue === undefined ? field.defaultValue : parsedDefaultValue,
    };
  }

  if (field.type === 'number') {
    if (field.defaultValue === '') {
      return { hasDefault: false };
    }

    const parsedDefaultValue = parseNumberDefaultValue(field.defaultValue);
    return {
      hasDefault: true,
      value: parsedDefaultValue === undefined ? field.defaultValue : parsedDefaultValue,
    };
  }

  if (
    field.type === 'any'
    || field.type === 'object'
    || field.type.startsWith('array_')
  ) {
    return {
      hasDefault: true,
      value: cloneSchemaFragment(field.defaultValue),
    };
  }

  return {
    hasDefault: true,
    value: field.defaultValue,
  };
};

const getPromptSchemaFieldDefaultValidationError = (
  field: PromptSchemaFieldDefinition,
): string | null => {
  const defaultValue = getPromptSchemaFieldDefaultValueForValidation(field);
  if (!defaultValue.hasDefault) {
    return null;
  }

  const fieldSchema = buildFieldSchemaFragment({
    ...field,
    defaultValue: undefined,
  });
  fieldSchema.default = cloneSchemaFragment(defaultValue.value);

  return validateSchemaDefault(fieldSchema, `Field "${field.name}"`);
};

export const getPromptSchemaFieldValidationError = (
  fields: PromptSchemaFieldDefinition[],
  fieldLabel: string,
  options: {
    allowAdditionalProperties?: PromptSchemaAdditionalProperties;
  } = {},
): string | null => {
  const validFields = fields.filter((field) => field.name.trim());
  if (validFields.length === 0) {
    if (options.allowAdditionalProperties !== undefined && options.allowAdditionalProperties !== false) {
      return null;
    }

    return `At least one ${fieldLabel} is required`;
  }

  const duplicates = getDuplicatePromptSchemaFieldNames(validFields);
  if (duplicates.length > 0) {
    return `Duplicate ${fieldLabel} names found: ${duplicates.join(', ')}`;
  }

  for (const field of validFields) {
    const fieldName = field.name.trim();
    const fieldNameValidationError = getPromptSchemaFieldNameValidationError(fieldName, fieldLabel, {
      includeContextLabel: false,
    });
    if (fieldNameValidationError) {
      return fieldNameValidationError;
    }

    if (field.type === 'enum' || field.type === 'array_enum') {
      const enumValues = getStringEnumValues(field.enumValues);
      if (enumValues.length === 0) {
        return `${getPromptSchemaFieldTypeLabel(field.type)} field "${field.name}" must include at least one option`;
      }

      const enumDuplicates = Array.from(
        new Set(
          enumValues.filter((value, index) => enumValues.indexOf(value) !== index),
        ),
      );
      if (enumDuplicates.length > 0) {
        return `${getPromptSchemaFieldTypeLabel(field.type)} field "${field.name}" has duplicate options: ${enumDuplicates.join(', ')}`;
      }
    }

    const advancedOptionsError = getPromptSchemaFieldAdvancedOptionsValidationError(field);
    if (advancedOptionsError) {
      return advancedOptionsError;
    }

    if (isObjectFieldType(field.type)) {
      const nestedFields = (field.nestedFields || []).filter((nestedField) => nestedField.name.trim());
      if (nestedFields.length === 0 && field.allowAdditionalProperties === false) {
        return `${getPromptSchemaFieldTypeLabel(field.type)} field "${field.name}" must include at least one nested field`;
      }

      const nestedDuplicates = getDuplicatePromptSchemaFieldNames(nestedFields);
      if (nestedDuplicates.length > 0) {
        return `Duplicate nested field names found for "${field.name}": ${nestedDuplicates.join(', ')}`;
      }

      if (nestedFields.length > 0) {
        const nestedError = getPromptSchemaFieldValidationError(nestedFields, fieldLabel);
        if (nestedError) {
          return nestedError;
        }
      }
    }

    const defaultValidationError = getPromptSchemaFieldDefaultValidationError(field);
    if (defaultValidationError) {
      return defaultValidationError;
    }
  }

  return null;
};
